import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import Calendar from "./Calendar";
import SeatSelection from "./SeatSelection";
import "../../../styles/TimeAndSeat.scss";

export interface Ticket {
  id: string;
  ticket_type: string;
  price: string;
  available_slot: string;
  ticket_count?: number;
  capacity?: number;
}

interface Props {
  tickets: Ticket[];
  availableDates: string[];
  isSeatSection?: boolean;
  isSingleTicket?: boolean;

  /*
   * Para lang sa required
   * customer data page.
   */
  maxSelectableSeats?: number;
  showSeatLimitNote?: boolean;

  onDateSelect: (
    date: string
  ) => void;

  onTimeSelect: (
    time: string
  ) => void;

  onTicketSelect: (
    ticketType: string
  ) => void;

  onSeatSelect?: (
    seatIds: string[]
  ) => void;
}

interface AvailableTime {
  value: string;
  label: string;
  timestamp: number;
}

const TimeAndSeat: React.FC<
  Props
> = ({
  tickets,
  availableDates,
  isSeatSection = false,
  isSingleTicket = false,
  maxSelectableSeats,
  showSeatLimitNote = false,
  onDateSelect,
  onTimeSelect,
  onTicketSelect,
  onSeatSelect,
}) => {
  const [
    selectedDate,
    setSelectedDate,
  ] = useState<string | null>(
    null
  );

  const [
    selectedTime,
    setSelectedTime,
  ] = useState<string | null>(
    null
  );

  const [
    selectedTicket,
    setSelectedTicket,
  ] = useState<string | null>(
    null
  );

  const [
    selectedSeats,
    setSelectedSeats,
  ] = useState<string[]>([]);

  const getDateKey = (
    dateValue: string | Date
  ): string => {
    const date =
      dateValue instanceof Date
        ? dateValue
        : new Date(dateValue);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return "";
    }

    const year =
      date.getFullYear();

    const month = String(
      date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
      date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const formatTime = (
    dateValue: string
  ): string => {
    const date =
      new Date(dateValue);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return "";
    }

    return date.toLocaleTimeString(
      "en-US",
      {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }
    );
  };

  const validAvailableDates =
    useMemo(() => {
      if (
        !Array.isArray(
          availableDates
        )
      ) {
        return [];
      }

      return [...availableDates]
        .filter(
          (date) =>
            !Number.isNaN(
              new Date(
                date
              ).getTime()
            )
        )
        .sort(
          (
            firstDate,
            secondDate
          ) =>
            new Date(
              firstDate
            ).getTime() -
            new Date(
              secondDate
            ).getTime()
        );
    }, [availableDates]);

  const firstAvailableDate =
    validAvailableDates[0] ||
    null;

  useEffect(() => {
    setSelectedTime(null);
    onTimeSelect("");

    setSelectedSeats([]);
    onSeatSelect?.([]);

    setSelectedTicket(null);
    onTicketSelect("");

    if (
      isSingleTicket &&
      firstAvailableDate
    ) {
      setSelectedDate(
        firstAvailableDate
      );

      onDateSelect(
        firstAvailableDate
      );

      return;
    }

    setSelectedDate(null);
    onDateSelect("");
  }, [
    isSingleTicket,
    firstAvailableDate,
    isSeatSection,
    onDateSelect,
    onTimeSelect,
    onTicketSelect,
    onSeatSelect,
  ]);

  /*
   * Kapag nabawasan ang ticket/customer
   * count, automatic na bawasan ang
   * selected seats para hindi lumampas
   * sa bagong seat limit.
   */
  useEffect(() => {
    if (
      !isSeatSection ||
      typeof maxSelectableSeats !==
        "number" ||
      maxSelectableSeats < 0
    ) {
      return;
    }

    setSelectedSeats(
      (previousSeats) => {
        if (
          previousSeats.length <=
          maxSelectableSeats
        ) {
          return previousSeats;
        }

        const updatedSeats =
          previousSeats.slice(
            0,
            maxSelectableSeats
          );

        onSeatSelect?.(
          updatedSeats
        );

        const selectedSeatNames =
          tickets
            .filter((ticket) =>
              updatedSeats.includes(
                ticket.id
              )
            )
            .map(
              (ticket) =>
                ticket.ticket_type
            )
            .join(", ");

        onTicketSelect(
          selectedSeatNames
        );

        return updatedSeats;
      }
    );
  }, [
    isSeatSection,
    maxSelectableSeats,
    tickets,
    onSeatSelect,
    onTicketSelect,
  ]);

  const handleDateSelect = (
    date: string
  ) => {
    if (isSingleTicket) {
      return;
    }

    setSelectedDate(date);
    setSelectedTime(null);

    setSelectedSeats([]);
    setSelectedTicket(null);

    onDateSelect(date);
    onTimeSelect("");
    onSeatSelect?.([]);
    onTicketSelect("");
  };

  const handleTimeSelect = (
    time: string
  ) => {
    if (!selectedDate) {
      return;
    }

    setSelectedTime(time);
    setSelectedSeats([]);
    setSelectedTicket(null);

    onTimeSelect(time);
    onSeatSelect?.([]);
    onTicketSelect("");
  };

  const handleTicketSelect = (
    ticket: Ticket
  ) => {
    const availableSlots =
      Number(
        ticket.available_slot ||
          0
      );

    if (availableSlots <= 0) {
      return;
    }

    setSelectedTicket(
      ticket.ticket_type
    );

    onTicketSelect(
      ticket.ticket_type
    );
  };

  const handleSeatSelect = (
    ticket: Ticket
  ) => {
    const availableSlots =
      Number(
        ticket.available_slot ||
          0
      );

    if (
      availableSlots <= 0 ||
      !selectedDate ||
      !selectedTime
    ) {
      return;
    }

    setSelectedSeats(
      (previousSeats) => {
        const isAlreadySelected =
          previousSeats.includes(
            ticket.id
          );

        if (
          !isAlreadySelected &&
          typeof maxSelectableSeats ===
            "number" &&
          maxSelectableSeats >= 0 &&
          previousSeats.length >=
            maxSelectableSeats
        ) {
          return previousSeats;
        }

        const updatedSeats =
          isAlreadySelected
            ? previousSeats.filter(
                (seatId) =>
                  seatId !==
                  ticket.id
              )
            : [
                ...previousSeats,
                ticket.id,
              ];

        onSeatSelect?.(
          updatedSeats
        );

        const selectedSeatNames =
          tickets
            .filter((item) =>
              updatedSeats.includes(
                item.id
              )
            )
            .map(
              (item) =>
                item.ticket_type
            )
            .join(", ");

        onTicketSelect(
          selectedSeatNames
        );

        return updatedSeats;
      }
    );
  };

  const availableTimes =
    useMemo<AvailableTime[]>(
      () => {
        const filteredDates =
          selectedDate
            ? validAvailableDates.filter(
                (dateValue) =>
                  getDateKey(
                    dateValue
                  ) ===
                  getDateKey(
                    selectedDate
                  )
              )
            : validAvailableDates;

        const times =
          filteredDates
            .map(
              (dateValue) => {
                const parsedDate =
                  new Date(
                    dateValue
                  );

                const formattedTime =
                  formatTime(
                    dateValue
                  );

                return {
                  value:
                    formattedTime,
                  label:
                    formattedTime,
                  timestamp:
                    parsedDate.getTime(),
                };
              }
            )
            .filter(
              (time) =>
                time.label
            )
            .sort(
              (
                firstTime,
                secondTime
              ) =>
                firstTime.timestamp -
                secondTime.timestamp
            );

        return times.filter(
          (
            time,
            index,
            array
          ) =>
            array.findIndex(
              (item) =>
                item.label ===
                time.label
            ) === index
        );
      },
      [
        validAvailableDates,
        selectedDate,
      ]
    );

  const seatSelectionDisabled =
    !selectedDate ||
    !selectedTime;

  return (
    <div className="time-seat-section">
      <Calendar
        availableDates={
          validAvailableDates
        }
        selectedDate={
          selectedDate
        }
        isSingleTicket={
          isSingleTicket
        }
        onSelect={
          handleDateSelect
        }
      />

      <div className="selection-group">
        <div className="selection-heading">
          <h3>Select Time</h3>

          {!selectedDate && (
            <span className="selection-note">
              Select a date to
              continue
            </span>
          )}
        </div>

        {availableTimes.length >
        0 ? (
          <div
            className={`radio-card-grid ${
              !selectedDate
                ? "disabled-group"
                : ""
            }`}
          >
            {availableTimes.map(
              (time) => {
                const isSelected =
                  selectedTime ===
                  time.value;

                return (
                  <label
                    key={time.value}
                    className={[
                      "radio-card",
                      "time-radio-card",
                      isSelected
                        ? "selected"
                        : "",
                      !selectedDate
                        ? "disabled"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <input
                      type="radio"
                      name="event-time"
                      value={
                        time.value
                      }
                      checked={
                        isSelected
                      }
                      disabled={
                        !selectedDate
                      }
                      onChange={() =>
                        handleTimeSelect(
                          time.value
                        )
                      }
                    />

                    <span className="custom-radio" />

                    <span className="radio-card-content">
                      <span className="radio-card-title">
                        {time.label}
                      </span>
                    </span>
                  </label>
                );
              }
            )}
          </div>
        ) : (
          <div className="selection-empty">
            No available time.
          </div>
        )}
      </div>

      <div
        className={
          isSeatSection
            ? "selection-group seat-selection-group"
            : "selection-group"
        }
      >
        <div className="selection-heading">
          <h3>
            {isSeatSection
              ? "Select Seat/s"
              : "Select Ticket Type"}
          </h3>

          {isSeatSection &&
            seatSelectionDisabled && (
              <span className="selection-note">
                Select date and
                time first
              </span>
            )}
        </div>

        {tickets.length > 0 ? (
          isSeatSection ? (
            <SeatSelection
              tickets={tickets}
              selectedSeatIds={
                selectedSeats
              }
              disabled={
                seatSelectionDisabled
              }
              maxSelectableSeats={
                maxSelectableSeats
              }
              showSeatLimitNote={
                showSeatLimitNote
              }
              onSeatSelect={
                handleSeatSelect
              }
            />
          ) : (
            <div className="radio-card-grid ticket-grid">
              {tickets.map(
                (ticket) => {
                  const availableSlots =
                    Number(
                      ticket.available_slot ||
                        0
                    );

                  const isSoldOut =
                    availableSlots <=
                    0;

                  const isSelected =
                    selectedTicket ===
                    ticket.ticket_type;

                  return (
                    <label
                      key={
                        ticket.id
                      }
                      className={[
                        "radio-card",
                        "ticket-radio-card",
                        isSelected
                          ? "selected"
                          : "",
                        isSoldOut
                          ? "disabled sold-out"
                          : "",
                      ]
                        .filter(
                          Boolean
                        )
                        .join(" ")}
                    >
                      <input
                        type="radio"
                        name="ticket-type"
                        value={
                          ticket.ticket_type
                        }
                        checked={
                          isSelected
                        }
                        disabled={
                          isSoldOut
                        }
                        onChange={() =>
                          handleTicketSelect(
                            ticket
                          )
                        }
                      />

                      <span className="custom-radio" />

                      <span className="radio-card-content">
                        <span className="radio-card-title">
                          {
                            ticket.ticket_type
                          }
                        </span>

                        <span className="ticket-price">
                          ₱
                          {parseFloat(
                            ticket.price ||
                              "0"
                          ).toLocaleString(
                            "en-PH",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}
                        </span>

                        <span className="radio-card-meta">
                          {isSoldOut
                            ? "Sold Out"
                            : `${availableSlots} Slot${
                                availableSlots !==
                                1
                                  ? "s"
                                  : ""
                              } Available`}
                        </span>
                      </span>
                    </label>
                  );
                }
              )}
            </div>
          )
        ) : (
          <div className="selection-empty">
            {isSeatSection
              ? "No available seats."
              : "No available ticket types."}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeAndSeat;